import React, { useEffect, useState, useCallback } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { subscribeAgentReconnect } from '../agentReconnectStore';

/**
 * "Phone not working" warning — shown only when the IVR skill-route queue
 * has tried to auto-reconnect the agent's softphone/WebRTC and failed
 * after the full timeout. The happy path (auto-reconnect succeeds, call
 * rings) produces NO banner.
 *
 * Backend publishes the failure event to /SSE/AgentReconnect with payload
 * shape: {type:"failed", did, callerNum, message, kind?}. kind "outbound"
 * marks an agent's own dial that was refused (DNC / blocked number, phone
 * down), so callerNum is the number they dialled. Anything older or with a
 * different shape (e.g. legacy pending payloads from rolling restarts) is
 * ignored.
 */
const AgentReconnectBanner = () => {
  const [warning, setWarning] = useState(null); // {message, did, callerNum} or null

  useEffect(() => {
    const unsub = subscribeAgentReconnect((event) => {
      if (!event || event.type !== 'failed') return;
      setWarning({
        message: event.message || 'Your phone is not working. Please contact support.',
        did: event.did || '',
        callerNum: event.callerNum || '',
        outbound: event.kind === 'outbound',
      });
    });
    return unsub;
  }, []);

  const dismiss = useCallback(() => setWarning(null), []);

  if (!warning) return null;

  return (
    <>
      <BannerStyles />
      <div className="vbar-root" role="alert" aria-live="assertive">
        <div className="vbar-icon"><FaExclamationTriangle /></div>
        <div className="vbar-text">
          <div className="vbar-title">{warning.message}</div>
          {(warning.callerNum || warning.did) && (
            <div className="vbar-sub">
              {warning.outbound ? 'Dialled number ' : 'Missed call from '}{warning.callerNum || '(unknown)'}
              {warning.did ? ` on DID ${warning.did}` : ''}
            </div>
          )}
        </div>
        <button className="vbar-btn vbar-btn-ghost" onClick={dismiss} aria-label="Dismiss">
          <FaTimes size={12} />
        </button>
      </div>
    </>
  );
};

const BannerStyles = () => (
  <style>{`
    .vbar-root {
      position: fixed;
      top: 56px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1200;
      max-width: 720px;
      width: calc(100vw - 32px);
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 10px 14px;
      background: var(--bg-surface);
      border: 1px solid var(--warning);
      border-left: 4px solid var(--warning);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-lg);
      animation: vbar-slide-in 0.2s ease-out;
    }
    @keyframes vbar-slide-in {
      from { opacity: 0; transform: translate(-50%, -10px); }
      to   { opacity: 1; transform: translate(-50%, 0); }
    }
    .vbar-icon {
      display: flex; align-items: center; justify-content: center;
      width: 32px; height: 32px;
      background: var(--warning-bg); color: var(--warning);
      border-radius: var(--radius-full);
      flex-shrink: 0;
    }
    .vbar-text { flex: 1; min-width: 0; }
    .vbar-title {
      font-weight: 600;
      color: var(--text-primary);
      font-size: var(--text-base);
    }
    .vbar-sub {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      margin-top: 2px;
    }
    .vbar-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 8px;
      border-radius: var(--radius-md);
      font-size: var(--text-sm); font-weight: 600;
      cursor: pointer;
      transition: background var(--transition-fast);
      border: 1px solid transparent;
      background: transparent;
      color: var(--text-muted);
    }
    .vbar-btn:hover { background: var(--bg-muted); color: var(--text-primary); }

    @media (max-width: 600px) {
      .vbar-root { top: 8px; gap: 8px; padding: 8px 10px; }
      .vbar-title { font-size: var(--text-sm); }
      .vbar-sub { font-size: var(--text-xs); }
    }
  `}</style>
);

export default AgentReconnectBanner;
